import { any, func, bool, string } from "prop-types";
import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import ReadingStatus from "../ReadingListDownload/reading_status";
import {
  submitDnataForm,
  updateUserInProfileSilent,
  uploadDnataPdf,
} from "../../redux/actions/index";
import { getDnataForm } from "../../api/profileApi";
import OnboardingListItem from "../OnboardingListItem/OnboardingListItem";
import * as style from "../ReadingListDownload/styles.module.scss";
import styles from "./styles.scss";
import Spinner from "../../common/Spinner";
import { Button } from "../../common";
import ProfileImageUpload from "../../containers/ProfileImage";
import { triggerDownload } from "../../common/OnboardingSectionCreator/helpers";

const DnataOnboarding = ({
  appData,
  dnataForms,
  submitDnataFormFunc,
  extraFormLoading,
  updateProfile,
  uploadDnataPdfFunc,
}) => {
  const { dnataOnboarding } = { ...{ dnataOnboarding: {} }, ...appData };
  const submitted = dnataOnboarding && dnataOnboarding.submitted;
  const results =
    dnataOnboarding && dnataOnboarding.test && dnataOnboarding.test.dnataTest;
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [curTypeCode, setCurTypeCode] = useState("");
  const FormStatus = Object.freeze({
    Todo: 1,
    Done: 3,
  });
  const typeCodes = {
    emergencyContactForm: "YDN5",
    equalOpportunitiesForm: "YDN6",
    wishForm: "YDN7",
    healthQuestionnaire: "YDN8",
    noticeForm: "YDN9",
    timeRegulationForm: "YDN0",
  };
  const isFormCompleted = (typeCode) =>
    !!(dnataOnboarding && dnataOnboarding[typeCode]);

  const checkIfExtraFormLoading = (typeCode) => extraFormLoading === typeCode;

  useEffect(() => {
    setLoading(false);
  }, [submitted]);

  const savePdf = async (pdfFileData, imageFileKey) => {
    const { candidateRef, emailAddress } = appData;
    const updatedUserData = {
      candidateRef,
      emailAddress,
      [imageFileKey]: pdfFileData,
    };

    await updateProfile(candidateRef, updatedUserData);
    uploadDnataPdfFunc(imageFileKey);
  };

  const processPDF = (pdfData, inputName) => {
    const base64Marker = ";base64,";
    const pdfBase64Data = pdfData.split(base64Marker)[1];
    return savePdf(pdfBase64Data, inputName);
  };

  const onPDFLoad = (readerEvent, inputName) => {
    // Access the file name here
    // Process the PDF data
    return processPDF(readerEvent.target.result, inputName);
  };

  const handlePdfChange = (event) => {
    const file = event.target.files[0];
    const inputName = event.target.name;
    const reader = new FileReader();
    reader.onload = (readerEvent) => onPDFLoad(readerEvent, inputName);
    reader.readAsDataURL(file);
    return reader;
  };

  const formsListWithStatus = [
    {
      status: dnataOnboarding.about ? ReadingStatus.Done : ReadingStatus.Todo,
      title: "About You",
      link: "about",
    },
    {
      status: dnataOnboarding.employment
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
      title: "Employment",
      link: "employment",
    },
    {
      status: dnataOnboarding.references
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
      title: "References",
      link: "references",
    },
    {
      status: dnataOnboarding.information
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
      title: "Additional Information",
      link: "information",
    },
    {
      status: dnataOnboarding.declaration
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
      title: "Declaration",
      link: "declaration",
    },
  ];

  const extraFormsListWithStatus = [
    {
      status: isFormCompleted(typeCodes.emergencyContactForm)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.emergencyContactForm,
      disabled: checkIfExtraFormLoading(typeCodes.emergencyContactForm),
      title: "Emergency Contact Form",
      text: "Enter Details",
      url: "dnata-onboarding/emergency-contact-form",
      click: isFormCompleted(typeCodes.emergencyContactForm)
        ? () =>
            triggerDownload(
              typeCodes.emergencyContactForm,
              "Dnata Emergency Contact Form.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
    {
      status: isFormCompleted(typeCodes.equalOpportunitiesForm)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.equalOpportunitiesForm,
      disabled: checkIfExtraFormLoading(typeCodes.equalOpportunitiesForm),
      title: "Equal Opportunities Form",
      text: "Enter Details",
      url: "dnata-onboarding/equal-opportunities-form",
      click: isFormCompleted(typeCodes.equalOpportunitiesForm)
        ? () =>
            triggerDownload(
              typeCodes.equalOpportunitiesForm,
              "Dnata Equal Opportunities Form.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
    {
      status: isFormCompleted(typeCodes.wishForm)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.wishForm,
      disabled: checkIfExtraFormLoading(typeCodes.wishForm),
      title: "Expression of Wish Form",
      text: "Enter Details",
      url: "dnata-onboarding/wish-form",
      click: isFormCompleted(typeCodes.wishForm)
        ? () =>
            triggerDownload(
              typeCodes.wishForm,
              "Dnata Expression of Wish Form.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
    {
      status: isFormCompleted(typeCodes.healthQuestionnaire)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.healthQuestionnaire,
      disabled: checkIfExtraFormLoading(typeCodes.healthQuestionnaire),
      title: "Health Questionnaire",
      text: "Enter Details",
      url: "dnata-onboarding/health-questionnaire",
      click: isFormCompleted(typeCodes.healthQuestionnaire)
        ? () =>
            triggerDownload(
              typeCodes.healthQuestionnaire,
              "Dnata Health Questionnaire.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
    {
      status: isFormCompleted(typeCodes.noticeForm)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.noticeForm,
      disabled: checkIfExtraFormLoading(typeCodes.noticeForm),
      title: "Notice to all Staff and Management",
      text: "Enter Details",
      url: "dnata-onboarding/notice-form",
      click: isFormCompleted(typeCodes.noticeForm)
        ? () =>
            triggerDownload(
              typeCodes.noticeForm,
              "Dnata Notice to Staff.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
    {
      status: isFormCompleted(typeCodes.timeRegulationForm)
        ? FormStatus.Done
        : FormStatus.Todo,
      loading: formLoading && curTypeCode === typeCodes.timeRegulationForm,
      disabled: checkIfExtraFormLoading(typeCodes.timeRegulationForm),
      title: "Working Time Regulation",
      text: "Enter Details",
      url: "dnata-onboarding/time-regulation-form",
      click: isFormCompleted(typeCodes.timeRegulationForm)
        ? () =>
            triggerDownload(
              typeCodes.timeRegulationForm,
              "Dnata Working Time Regulations.pdf",
              setCurTypeCode,
              setFormLoading
            )
        : null,
    },
  ];

  const canSubmit = formsListWithStatus.every(
    (doc) => doc.status === ReadingStatus.Done
  );

  const handleSubmit = () => {
    setLoading(true);
    submitDnataFormFunc(appData.dnataOnboarding);
  };

  const downloadApplicationForm = () => {
    setLoading(true);
    getDnataForm().then((response) => {
      const link = document.createElement("a");
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });
      link.href = window.URL.createObjectURL(blob);
      link.download = `Dnata Application Form.pdf`;
      link.click();
      setLoading(false);
    });
  };

  const determineLeftSide = (status) => {
    if (submitted) {
      return 3;
    }
    if (status === 2) {
      return 4;
    }
    return status;
  };

  const determineRightSide = (status) => {
    if (status === 2) {
      return 4;
    }
    return status;
  };

  return (
    <div className="container text-bluishGray pb-2">
      <header className="row">
        <div className="col-12">
          <h2 className="section-header pt-2">Dnata Onboarding</h2>
        </div>
      </header>
      <div className="row">
        <div className="col-lg-6">
          <div className={`section-body bg-white p-1 my-1 mt-lg-0`}>
            <h5 className="px-1">Reference History Form</h5>
            <p className="p-1">
              Please download and complete the Dnata Reference History Form. If
              you have any queries about the form, please speak to your HRGO
              consultant.
            </p>
            <div>
              <div className="col-12 py-2">
                <div>
                  <div>
                    <div style={{ position: "relative" }}></div>
                    <a
                      target="_blank"
                      href="https://www.hrgo.co.uk/media/8201/dnata_refernence_history_form.pdf"
                      className="m-auto btn btn-block btn-info cursor-pointer mb-0"
                      style={{ width: "264px" }}
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <p className="p-1">
              Please upload the completed Dnata Reference History Form.
            </p>
            {dnataForms && dnataForms.dnataReferenceHistoryForm && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: "green",
                  padding: "1rem",
                }}
              >
                Thank you, your reference history form has been saved.
              </div>
            )}
            <ProfileImageUpload
              handleChange={handlePdfChange}
              showHeader={false}
              imageFileKey="dnataReferenceHistoryForm"
              redirectAfterSave={false}
              text="Upload reference history form"
              isDnata={true}
              allowPdf={true}
            />
          </div>

          <div className={`section-body bg-white p-1 my-1 mt-lg-0`}>
            <h5>Upload Signed Declaration Page</h5>
            <div>
              Please download and print the last page of your application form
              (Page 4 - Declaration).
              <br />
              Please sign and date the form and then take a straight picture of
              it ensuring the whole form is included in the picture
              <br />
              (must be able to see all 4 corners of the page).
              <br />
              You can then upload the picture below.
            </div>
            {appData.dnataOnboarding &&
              appData.dnataOnboarding.dnataDeclaration && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    color: "green",
                    padding: "1rem",
                  }}
                >
                  Thank you, your declaration has been saved.
                </div>
              )}
            <ProfileImageUpload
              showHeader={false}
              imageFileKey="dnataDeclaration"
              redirectAfterSave={false}
              text="Upload declaration page"
              isDnata={true}
            />
          </div>

          <div className={`section-body bg-white p-1 my-1 mt-lg-0`}>
            <h5>Upload OCA41</h5>
            <div>If applicable, please upload your OCA41 form.</div>
            {dnataForms && dnataForms.dnataOCA41 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: "green",
                  padding: "1rem",
                }}
              >
                Thank you, your OCA41 form has been saved.
              </div>
            )}
            <ProfileImageUpload
              handleChange={handlePdfChange}
              showHeader={false}
              imageFileKey="dnataOCA41"
              redirectAfterSave={false}
              text="Upload OCA41 form"
              isDnata={true}
              allowPdf={true}
            />
          </div>
        </div>

        <div className="col-lg-6">
          <div className={`section-body bg-white py-1 my-1 mt-lg-0`}>
            <h5 className="px-1">Test</h5>
            <p className="p-1">
              Please download and complete the Dnata test. If you have any
              queries about the test, please speak to your HRGO consultant.
            </p>
            <div>
              <div className="col-12 py-2">
                <div>
                  <div>
                    <div style={{ position: "relative" }}></div>
                    <a
                      target="_blank"
                      href="https://www.hrgo.co.uk/media/8202/dnata_operations_recruitment_test.pdf"
                      className="m-auto btn btn-block btn-info cursor-pointer mb-0"
                      style={{ width: "264px" }}
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <p className="p-1">Please upload the completed Dnata test.</p>
            {dnataForms && dnataForms.dnataTest && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: "green",
                  padding: "1rem",
                }}
              >
                Thank you, your test has been saved.
              </div>
            )}
            <ProfileImageUpload
              handleChange={handlePdfChange}
              showHeader={false}
              imageFileKey="dnataTest"
              redirectAfterSave={false}
              text="Upload test"
              isDnata={true}
              allowPdf={true}
            />
          </div>
          <div className="section-body pt-1 mb-2 bg-white">
            <h5 className="px-1">Additional Forms</h5>
            <p className="p-1">
              Please complete each of the below forms. Once you have submitted
              your forms you will be unable to edit them. If you have any
              queries about the forms, please speak to your HRGO Consultant.
            </p>
            {extraFormsListWithStatus.map((elem, i) => (
              <OnboardingListItem
                key={i}
                title={elem.title}
                status={elem.status}
                btnText={elem.text}
                url={elem.url}
                loading={elem.loading}
                handleClick={elem.click}
                disabled={elem.disabled}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="w-100 d-flex justify-content-end">
        <Link to="/profile" className="btn btn-primary px-3">
          Back to My Account
        </Link>
      </div>
    </div>
  );
};

DnataOnboarding.propTypes = {
  appData: any,
  dnataForms: any,
  submitDnataFormFunc: func,
  dnataTestLoading: bool,
  extraFormLoading: string,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
  dnataForms: state.core.dnata,
  dnataTestLoading: state.core.dnataTest.isLoading,
  extraFormLoading: state.core.dnata.formLoading,
});

const mapDispatchToProps = {
  submitDnataFormFunc: submitDnataForm,
  uploadDnataPdfFunc: uploadDnataPdf,
  updateProfile: updateUserInProfileSilent,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(DnataOnboarding);
